/**
 * Vocabulário de tela de planos e assinatura.
 *
 * O que ESTA camada faz: TRADUZ (chave técnica → palavra em português),
 * CLASSIFICA (comparar dois números que já vieram prontos) e FORMATA (centavos
 * → `R$ 34,00`).
 *
 * O que ela **não** faz, nunca: preço mensal equivalente e porcentagem de
 * desconto. Os dois saem do servidor (`monthlyEquivalentCents`,
 * `discountPercent`) e a tela imprime — invariante 3 do CLAUDE.md. Um "−20%"
 * escrito no JSX continua certo na tela e errado no extrato depois do primeiro
 * reajuste, e ninguém reclama porque a tela parece certa.
 *
 * Também não há `if (code === 'black')` em lugar nenhum: o destaque do plano é
 * o campo `badge`, que é DADO (invariante 4).
 */
import { formatCurrency, formatNumber } from '@mgdlead/ui';

import type { BillingInterval, CatalogPlan, PlanPrice, UsageRow } from '../../lib/api';

/* ========================================================================== */
/*  Ciclos                                                                     */
/* ========================================================================== */

export interface CicloDeCobranca {
  interval: BillingInterval;
  label: string;
  /** Como a frase "cobrado a cada X" fica embaixo do preço. */
  cobranca: string;
}

/**
 * A ordem do seletor. É uma lista de APRESENTAÇÃO (nome e frase), não de
 * preço: quanto custa cada ciclo é sempre o que o servidor mandou em `prices`.
 * Ciclo que o servidor não oferecer simplesmente não aparece.
 */
export const CICLOS: readonly CicloDeCobranca[] = [
  { interval: 'MONTHLY', label: 'Mensal', cobranca: 'cobrado todo mês' },
  { interval: 'QUARTERLY', label: 'Trimestral', cobranca: 'cobrado a cada 3 meses' },
  { interval: 'SEMIANNUAL', label: 'Semestral', cobranca: 'cobrado a cada 6 meses' },
  { interval: 'YEARLY', label: 'Anual', cobranca: 'cobrado uma vez por ano' },
];

export function rotuloDeCiclo(interval: BillingInterval | null): string | null {
  if (interval === null) return null;
  return CICLOS.find((ciclo) => ciclo.interval === interval)?.label ?? interval;
}

/** O preço daquele plano naquele ciclo. Seleção, não conta. */
export function precoDoCiclo(plan: CatalogPlan, interval: BillingInterval): PlanPrice | null {
  return plan.prices.find((preco) => preco.interval === interval) ?? null;
}

/**
 * O link de checkout externo (Kiwify) da oferta escolhida, ou `null`.
 *
 * `null` **não** é "compre depois": é o estado em que o botão continua
 * registrando a intenção e avisando que o pagamento ainda não está disponível.
 * Quem lê este `null` tem de escolher outra frase — não uma URL vazia que faz o
 * botão parecer igual nos dois casos e abrir o nada.
 */
export function checkoutDoCiclo(plan: CatalogPlan, interval: BillingInterval): string | null {
  return precoDoCiclo(plan, interval)?.checkoutUrl ?? null;
}

/**
 * Já dá para pagar por aqui? Responde olhando o CATÁLOGO, nunca uma constante.
 *
 * O dono preenche os vinte links da Kiwify aos poucos, um plano de cada vez. Um
 * booleano fixo no código diria "pagamento indisponível" no dia seguinte ao
 * primeiro link entrar — rótulo de fase anterior que continua na tela porque
 * ninguém reclama de uma frase que parece certa.
 */
export function algumCheckoutDisponivel(
  plans: readonly CatalogPlan[],
  interval: BillingInterval,
): boolean {
  return plans.some((plan) => checkoutDoCiclo(plan, interval) !== null);
}

/** Ciclos que pelo menos um plano oferece, na ordem de `CICLOS`. */
export function ciclosDisponiveis(plans: readonly CatalogPlan[]): CicloDeCobranca[] {
  return CICLOS.filter((ciclo) =>
    plans.some((plan) => plan.prices.some((preco) => preco.interval === ciclo.interval)),
  );
}

export interface DescontoDoCiclo {
  /** Inteiro, exatamente como o servidor mandou. */
  percent: number;
  /**
   * `true` quando os planos têm descontos DIFERENTES nesse ciclo — aí o rótulo
   * vira "até 8%". O número continua sendo um dos que o servidor mandou; o que
   * a tela escolhe é qual deles mostrar, e "até" é o que a torna verdadeira
   * para todos os planos ao mesmo tempo.
   */
  aproximado: boolean;
}

/**
 * O desconto que aparece ao lado do nome do ciclo no seletor.
 *
 * `null` quando nenhum plano tem desconto naquele ciclo (é o caso do mensal:
 * não há do que descontar). Zero não é ausência — se o servidor mandar `0`,
 * a tela mostra `0%`, porque isso é uma afirmação sobre o preço.
 */
export function descontoDoCiclo(
  plans: readonly CatalogPlan[],
  interval: BillingInterval,
): DescontoDoCiclo | null {
  const percentuais = plans
    .map((plan) => precoDoCiclo(plan, interval)?.discountPercent ?? null)
    .filter((valor): valor is number => valor !== null);

  if (percentuais.length === 0) return null;

  const maior = percentuais.reduce((a, b) => (b > a ? b : a), percentuais[0] as number);
  const todosIguais = percentuais.every((valor) => valor === percentuais[0]);
  return { percent: maior, aproximado: !todosIguais };
}

/** `"8% off"` ou `"até 8% off"`. */
export function rotuloDeDesconto(desconto: DescontoDoCiclo): string {
  return `${desconto.aproximado ? 'até ' : ''}${desconto.percent}% off`;
}

/* ========================================================================== */
/*  Dinheiro                                                                   */
/* ========================================================================== */

/**
 * Centavos → `R$ 34,00`.
 *
 * Dividir por 100 é conversão de UNIDADE do número que o servidor mandou, não
 * cálculo de métrica: não existe um segundo caminho para chegar nesse valor.
 * O que seria cálculo — dividir o total pelos meses, tirar a porcentagem de
 * desconto — não acontece aqui em lugar nenhum.
 *
 * **A moeda é a do preço**, não `BRL` escrito na tela: `plan_prices.currency`
 * existe no banco e viaja no catálogo. Fixar "R$" aqui faria a tela continuar
 * dizendo real no dia em que alguém cadastrasse uma oferta em outra moeda — e o
 * número ficaria certo com o símbolo errado, que é pior do que não mostrar.
 */
export function precoFormatado(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency);
}

/** `R$ 34,00/mês` — o equivalente mensal que o SERVIDOR calculou. */
export function mensalEquivalente(preco: PlanPrice): string {
  return `${precoFormatado(preco.monthlyEquivalentCents, preco.currency)}/mês`;
}

/** O preço cheio daquele ciclo, com a moeda dele. */
export function totalFormatado(preco: PlanPrice): string {
  return precoFormatado(preco.totalCents, preco.currency);
}

/* ========================================================================== */
/*  Chaves de recurso                                                          */
/* ========================================================================== */

/**
 * Chave técnica → palavra de gente.
 *
 * **A LISTA não mora aqui** — quem manda quais linhas existem, e em que ordem,
 * é o servidor (`featureKeys`). Este mapa só traduz. Chave que o servidor
 * mandar e este mapa não conhecer aparece com o nome cru, e não some da tela:
 * sumir em silêncio é como uma linha inteira da tabela comparativa deixa de
 * existir sem ninguém perceber.
 */
export const ROTULO_DA_CHAVE: Readonly<Record<string, string>> = {
  max_monitors: 'Números de WhatsApp',
  max_groups: 'Grupos',
  max_campaigns: 'Campanhas',
  max_redirectors: 'Redirecionadores',
  max_members: 'Pessoas na equipe',
  max_meta_accounts: 'Contas de anúncio da Meta',
  meta_ads_enabled: 'Integração com Meta Ads',
};

export function rotuloDaChave(key: string): string {
  return ROTULO_DA_CHAVE[key] ?? key;
}

export const ILIMITADO = 'Ilimitado';
export const NAO_INCLUSO = 'Não incluso';

/** `-1` = ilimitado. Nunca deixar `-1` nem vazio chegarem na tela. */
export function ehIlimitado(valor: number): boolean {
  return valor === -1;
}

/**
 * O valor de um recurso do plano, em português.
 *
 * Três casos que as telas erram e aqui não erram:
 *  - `-1` é **"Ilimitado"**, nunca `-1` e nunca vazio;
 *  - `0` é **"Não incluso"** — um fato, diferente de ausência de dado;
 *  - chave ausente é ausência de dado, e devolve `null` para quem chama mostrar
 *    o travessão (invariante 8).
 */
export function descreverRecurso(valor: number | boolean | string | undefined): string | null {
  if (valor === undefined) return null;
  if (typeof valor === 'boolean') return valor ? 'Incluso' : NAO_INCLUSO;
  if (typeof valor === 'string') return valor.length === 0 ? null : valor;
  if (ehIlimitado(valor)) return ILIMITADO;
  if (valor === 0) return NAO_INCLUSO;
  return formatNumber(valor, { abbreviate: 'never' });
}

/* ========================================================================== */
/*  Botão do plano                                                             */
/* ========================================================================== */

export type AcaoDoPlano = 'trial' | 'assinar';

/** Quem decide o texto é o `trialDays` do servidor, não o código do plano. */
export function acaoDoPlano(plan: Pick<CatalogPlan, 'trialDays'>): AcaoDoPlano {
  return plan.trialDays > 0 ? 'trial' : 'assinar';
}

export function rotuloDoBotao(plan: Pick<CatalogPlan, 'trialDays'>): string {
  return acaoDoPlano(plan) === 'trial' ? `Testar grátis ${plan.trialDays} dias` : 'Assinar';
}

/* ========================================================================== */
/*  Assinatura vigente                                                         */
/* ========================================================================== */

export interface EstadoDaAssinatura {
  label: string;
  tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
}

/**
 * Os estados que o contrato prevê. `null` (nunca assinou) NÃO está aqui de
 * propósito: quem trata isso é a tela, porque "nunca assinou" não é um estado
 * de assinatura — é a ausência dela.
 */
export const ESTADO_DA_ASSINATURA: Readonly<Record<string, EstadoDaAssinatura>> = {
  TRIALING: { label: 'Em teste grátis', tone: 'info' },
  ACTIVE: { label: 'Ativa', tone: 'success' },
  PAST_DUE: { label: 'Pagamento em atraso', tone: 'warning' },
  CANCELED: { label: 'Cancelada', tone: 'neutral' },
  EXPIRED: { label: 'Expirada', tone: 'danger' },
};

export function estadoDaAssinatura(status: string | null): EstadoDaAssinatura | null {
  if (status === null) return null;
  return ESTADO_DA_ASSINATURA[status] ?? { label: status, tone: 'neutral' };
}

/* ========================================================================== */
/*  Consumo contra o limite                                                    */
/* ========================================================================== */

/**
 * Quanto da barra pintar (0..1), ou `null` quando barra nenhuma faz sentido.
 *
 * Isto NÃO é métrica: é largura de elemento, do mesmo tipo que a `OccupancyBar`
 * dos grupos faz. Os dois números (`current`, `limit`) chegaram prontos do
 * servidor, e o número que a pessoa lê aparece escrito ao lado — a barra é o
 * segundo canal, nunca o único.
 *
 * `null` em dois casos honestos: ilimitado (não há teto para medir contra) e
 * limite `0` (não incluso — uma barra vazia diria "ainda cabe", e não cabe).
 */
export function fracaoDoUso(row: Pick<UsageRow, 'current' | 'limit' | 'unlimited'>): number | null {
  if (row.unlimited || ehIlimitado(row.limit)) return null;
  if (row.limit <= 0) return null;
  return Math.min(1, Math.max(0, row.current / row.limit));
}

export type TomDoUso = 'success' | 'warning' | 'danger' | 'muted';

/** Classificação por faixa — comparação, não conta. */
export function tomDoUso(row: Pick<UsageRow, 'current' | 'limit' | 'unlimited' | 'allowed'>): TomDoUso {
  const fracao = fracaoDoUso(row);
  if (fracao === null) return 'muted';
  if (!row.allowed || fracao >= 1) return 'danger';
  if (fracao >= 0.8) return 'warning';
  return 'success';
}

/**
 * `"3 de 3"`, `"3 usados · ilimitado"`, `"0 · não incluso"`.
 *
 * Nunca escreve `-1` e nunca deixa o lugar vazio.
 */
export function rotuloDoUso(row: UsageRow): string {
  if (row.unlimited || ehIlimitado(row.limit)) {
    return `${formatNumber(row.current, { abbreviate: 'never' })} usados · ${ILIMITADO.toLocaleLowerCase('pt-BR')}`;
  }
  if (row.limit === 0) {
    return `${formatNumber(row.current, { abbreviate: 'never' })} · ${NAO_INCLUSO.toLocaleLowerCase('pt-BR')}`;
  }
  return `${formatNumber(row.current, { abbreviate: 'never' })} de ${formatNumber(row.limit, { abbreviate: 'never' })}`;
}

/** Quanto ainda cabe, em palavras. `null` quando não faz sentido dizer. */
export function rotuloDoQueSobra(row: UsageRow): string | null {
  if (row.unlimited || ehIlimitado(row.limit)) return null;
  if (row.limit === 0) return null;
  if (!row.allowed || row.remaining <= 0) return 'sem vaga';
  return row.remaining === 1 ? 'resta 1' : `restam ${formatNumber(row.remaining, { abbreviate: 'never' })}`;
}

/**
 * Limite negociado fora da tabela do plano.
 *
 * Aparece NOMEADO, e não disfarçado de plano: existe override ativo em
 * produção, e um número que não bate com o plano anunciado, sem explicação na
 * tela, vira chamado de suporte — ou, pior, vira "o sistema está errado".
 */
export const EXCECAO_NEGOCIADA = 'Exceção negociada';
export const EXCECAO_NEGOCIADA_EXPLICACAO =
  'Este limite foi ajustado só para este workspace e não vem da tabela do plano.';

export function ehExcecao(row: Pick<UsageRow, 'source'>): boolean {
  return row.source === 'override';
}
